import { initializeApp, FirebaseApp, getApps, deleteApp } from 'firebase/app';
import { getAuth, Auth, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { apiService } from './apiService';
import { AppleSignInOption } from '../types/auth';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface ClientConfiguration {
  clientId: string;
  clientName: string;
  availableProviders: string[];
  securityPolicy: {
    tokenExpirationMinutes: number;
    sessionTimeoutMinutes: number;
    requireTwoFactor: boolean;
  };
  firebaseConfig: FirebaseConfig;
  /**
   * The Apple sign-in paths this client offers. More than one entry means the client's users are
   * split across Apple Developer teams and each needs its own button.
   */
  appleSignInOptions?: AppleSignInOption[];
}

/**
 * Service for dynamically initializing Firebase based on client configuration
 */
class DynamicFirebaseService {
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private googleProvider: GoogleAuthProvider | null = null;
  private appleProvider: OAuthProvider | null = null;
  private currentClientId: string | null = null;
  private configuration: ClientConfiguration | null = null;
  private initializationPromise: Promise<ClientConfiguration> | null = null;
  private initializingClientId: string | null = null;

  /**
   * Initialize Firebase with client-specific configuration.
   *
   * Concurrent callers share a single initialization. The cached-config check below reads instance
   * state, so without this every caller that arrives before the first one finishes starts its own
   * initialization - and each one tore down the app the previous had created, invalidating any Auth
   * reference already handed out. That surfaced mid-sign-in as
   * "Firebase App named '[DEFAULT]' already deleted".
   */
  async initializeWithClientId(clientId: string): Promise<ClientConfiguration> {
    // If already initialized for the same client, return cached config
    if (this.currentClientId === clientId && this.configuration) {
      return this.configuration;
    }

    if (this.initializationPromise && this.initializingClientId === clientId) {
      return this.initializationPromise;
    }

    this.initializingClientId = clientId;
    this.initializationPromise = this.initializeClient(clientId);

    try {
      return await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
      this.initializingClientId = null;
    }
  }

  /**
   * Performs a single client initialization
   */
  private async initializeClient(clientId: string): Promise<ClientConfiguration> {
    try {
      // Clean up previous Firebase instance if switching clients
      if (this.app && this.currentClientId !== clientId) {
        await this.cleanup();
      }

      // Fetch client configuration from backend
      const config = await this.fetchClientConfiguration(clientId);

      // Initialize Firebase with the client's configuration
      await this.initializeFirebase(config.firebaseConfig);

      // Store current configuration
      this.currentClientId = clientId;
      this.configuration = config;

      return config;
    } catch (error) {
      console.error('Failed to initialize Firebase with client configuration:', error);
      throw new Error(`Failed to initialize Firebase for client ${clientId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get the current Firebase app instance
   */
  getApp(): FirebaseApp {
    if (!this.app) {
      throw new Error('Firebase not initialized. Call initializeWithClientId() first.');
    }
    return this.app;
  }

  /**
   * Get the current Firebase Auth instance
   */
  getAuth(): Auth {
    if (!this.auth) {
      throw new Error('Firebase Auth not initialized. Call initializeWithClientId() first.');
    }
    return this.auth;
  }

  /**
   * Get the current Google Auth Provider
   */
  getGoogleProvider(): GoogleAuthProvider {
    if (!this.googleProvider) {
      throw new Error('Google Auth Provider not initialized. Call initializeWithClientId() first.');
    }
    return this.googleProvider;
  }

  /**
   * Get the current Apple Auth Provider
   */
  getAppleProvider(): OAuthProvider {
    if (!this.appleProvider) {
      throw new Error('Apple Auth Provider not initialized. Call initializeWithClientId() first.');
    }
    return this.appleProvider;
  }

  /**
   * Get the current client configuration
   */
  getCurrentConfiguration(): ClientConfiguration {
    if (!this.configuration) {
      throw new Error('No client configuration loaded. Call initializeWithClientId() first.');
    }
    return this.configuration;
  }

  /**
   * Check if Firebase is initialized for a specific client
   */
  isInitializedForClient(clientId: string): boolean {
    return this.currentClientId === clientId && this.app !== null;
  }

  /**
   * Clean up Firebase resources
   */
  async cleanup(): Promise<void> {
    if (this.app) {
      try {
        await deleteApp(this.app);
      } catch (error) {
        console.warn('Error cleaning up Firebase app:', error);
      }
    }
    
    this.app = null;
    this.auth = null;
    this.googleProvider = null;
    this.appleProvider = null;
    this.currentClientId = null;
    this.configuration = null;
  }

  /**
   * Fetch client configuration from backend
   */
  private async fetchClientConfiguration(clientId: string): Promise<ClientConfiguration> {
    try {
      const response = await apiService.getClientConfiguration(clientId);
      
      if (!response.firebaseConfig) {
        throw new Error('Firebase configuration not found in client configuration');
      }

      // Validate Firebase configuration
      this.validateFirebaseConfig(response.firebaseConfig);

      return response;
    } catch (error) {
      console.error('Failed to fetch client configuration:', error);
      throw new Error(`Failed to fetch configuration for client ${clientId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Initialize Firebase with the provided configuration
   */
  private async initializeFirebase(config: FirebaseConfig): Promise<void> {
    try {
      const existingApps = getApps();

      // Reuse an app already running the same project rather than deleting and recreating it.
      // deleteApp invalidates every Auth reference previously handed out, so recreating an
      // identical app is never a no-op - anything mid-flight fails with app/app-deleted.
      const reusable = existingApps.find(app =>
        app.options.projectId === config.projectId && app.options.apiKey === config.apiKey);

      if (reusable) {
        this.app = reusable;
        this.auth = getAuth(reusable);
        this.initializeProviders();

        console.log('Firebase already initialized, reusing app for client:', config.projectId);
        return;
      }

      // Only tear down apps belonging to a different project
      for (const app of existingApps) {
        await deleteApp(app);
      }

      this.app = initializeApp(config);
      this.auth = getAuth(this.app);
      this.initializeProviders();

      console.log('Firebase initialized successfully for client:', config.projectId);
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      throw new Error(`Firebase initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Builds the OAuth providers for the current app
   */
  private initializeProviders(): void {
    this.googleProvider = new GoogleAuthProvider();
    this.googleProvider.addScope('email');
    this.googleProvider.addScope('profile');
    this.googleProvider.setCustomParameters({
      prompt: 'select_account'
    });

    // Sign in with Apple via Firebase OAuth
    this.appleProvider = new OAuthProvider('apple.com');
    this.appleProvider.addScope('email');
    this.appleProvider.addScope('name');
  }

  /**
   * Validate Firebase configuration
   */
  private validateFirebaseConfig(config: FirebaseConfig): void {
    const requiredFields: (keyof FirebaseConfig)[] = [
      'apiKey',
      'authDomain', 
      'projectId',
      'appId'
    ];

    const missingFields = requiredFields.filter(field => !config[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required Firebase configuration fields: ${missingFields.join(', ')}`);
    }

    // Additional validation
    if (config.apiKey.length < 30) {
      console.warn('Firebase API key seems unusually short');
    }

    if (!config.authDomain.includes('firebaseapp.com') && !config.authDomain.includes('firebase.com')) {
      console.warn('Firebase Auth domain should typically contain firebaseapp.com or firebase.com');
    }
  }
}

// Export singleton instance
export const dynamicFirebaseService = new DynamicFirebaseService();
